import { Link, useOutletContext } from "@remix-run/react";
import { useMemo, useRef, useState } from "react";
import { useFlexScroll } from "./useFlexScroll";
import type { CartItem } from "~/routes/_storefront";
import type { ProductCardProduct, ProductCardSibling } from "~/components/storefront/ProductCard";
import { getColorHex, SIZE_ORDER } from "~/utils/storefront-constants";
import { resolveFeaturedImage } from "~/utils/productMedia";
import { formatStorefrontPrice } from "~/utils/money";
import { displayTitle as formatTitleForLocale } from "~/utils/productTitle";
import { getProductBaseName } from "~/utils/productSiblings";
import type { Locale } from "~/utils/locale";

/**
 * Horizontal product rail (Diolo rebrand 2026-06-10).
 *
 * The flat editorial alternative to the grid ProductCard: cells separated
 * by hairlines, product shot on a light grey field, one-line meta row
 * (title left / price right), colour subtitle, swatch dots + colour
 * count, and a "+" quick-add that reveals a size strip in place. Used on
 * the homepage between full-bleed editorial sections.
 */

function colourFromTitle(title: string): string | null {
  // Catalog titles are "Base Name - Colour (Vietnamese)" — pull the
  // colour token out; fall back to null for single-part titles.
  const base = getProductBaseName(title);
  if (!base || base === title) return null;
  const rest = title.slice(base.length).replace(/^\s*-\s*/, "");
  const colour = rest.replace(/\(.*?\)/g, "").trim();
  return colour || null;
}

function RailCell({
  product,
  siblings,
  locale,
}: {
  product: ProductCardProduct;
  siblings?: ProductCardSibling[];
  locale: Locale;
}) {
  const { addToCart, currency } = useOutletContext<{
    addToCart: (item: CartItem) => void;
    currency: string;
  }>();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [addedSize, setAddedSize] = useState<string | null>(null);

  const title = formatTitleForLocale(product.title, locale, product.titleEn ?? null);
  const baseTitle = getProductBaseName(title) || title;
  const colourName =
    siblings?.find((s) => s.isCurrent)?.color || colourFromTitle(product.title);

  const image = resolveFeaturedImage(product as any) || product.featuredImageUrl;
  const handle = product.handle || product.id;
  const price = product.retailPrice
    ? Number(product.retailPrice)
    : Number(product.variants[0]?.price || 0);

  // Distinct colourways for the dot row (own colour included).
  const swatches = useMemo(() => {
    if (!siblings || siblings.length === 0) {
      return colourName ? [{ id: product.id, color: colourName }] : [];
    }
    const seen = new Set<string>();
    return siblings.filter((s) => {
      if (!s.color || seen.has(s.color)) return false;
      seen.add(s.color);
      return true;
    });
  }, [siblings, colourName, product.id]);

  // Size options (mirrors ProductCard's option1/option2 detection).
  const rawOpt1 = [...new Set(product.variants.map((v) => v.option1).filter(Boolean))] as string[];
  const rawOpt2 = [...new Set(product.variants.map((v) => v.option2).filter(Boolean))] as string[];
  const opt1IsSize = rawOpt2.length === 0 && rawOpt1.length > 0 && rawOpt1.every((v) => v in SIZE_ORDER);
  const sizes = (opt1IsSize ? rawOpt1 : rawOpt2).sort(
    (a, b) => (SIZE_ORDER[a] ?? 99) - (SIZE_ORDER[b] ?? 99),
  );

  const sizeInStock = (size: string) =>
    product.variants.some(
      (v) => (opt1IsSize ? v.option1 === size : v.option2 === size) && v.inventoryQuantity > 0,
    );

  const quickAdd = (size: string) => {
    const variant = product.variants.find(
      (v) => (opt1IsSize ? v.option1 === size : v.option2 === size) && v.inventoryQuantity > 0,
    );
    if (!variant) return;
    addToCart({
      variantId: variant.id,
      productHandle: handle,
      productTitle: product.title,
      variantTitle: opt1IsSize ? size : `${variant.option1 || ""} / ${size}`.trim(),
      color: opt1IsSize ? "" : variant.option1 || "",
      size,
      price: Number(variant.price),
      comparePrice: variant.compareAtPrice ? Number(variant.compareAtPrice) : undefined,
      imageUrl: image || undefined,
      quantity: 1,
      maxQuantity: variant.inventoryQuantity,
    });
    setAddedSize(size);
    setTimeout(() => {
      setAddedSize(null);
      setQuickAddOpen(false);
    }, 1200);
  };

  return (
    <div className="df-rail-cell group">
      <div className="relative aspect-[4/5] overflow-hidden bg-[#f4f4f4]">
        <Link to={`/products/${handle}`} aria-label={baseTitle} className="df-rail-media" draggable={false}>
          {image ? (
            <img
              src={image}
              alt={baseTitle}
              width={480}
              height={600}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : null}
        </Link>

        {/* Quick-add: "+" toggles an in-place size strip */}
        {sizes.length > 0 && (
          <>
            <button
              type="button"
              className="df-quick-add"
              aria-label={locale === "vi" ? "Thêm nhanh" : "Quick add"}
              aria-expanded={quickAddOpen}
              onClick={(e) => {
                e.preventDefault();
                setQuickAddOpen((o) => !o);
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.25}>
                <path d="M8 1v14M1 8h14" strokeLinecap="round" />
              </svg>
            </button>
            <div
              className={`absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center justify-center gap-1 bg-white/95 px-2 py-2 transition-transform duration-200 ${
                quickAddOpen ? "translate-y-0" : "translate-y-full"
              }`}
            >
              {sizes.map((size) => {
                const inStock = sizeInStock(size);
                return (
                  <button
                    key={size}
                    type="button"
                    disabled={!inStock}
                    onClick={(e) => {
                      e.preventDefault();
                      quickAdd(size);
                    }}
                    className={`min-w-[2rem] px-1.5 py-1 text-[11px] transition-colors ${
                      addedSize === size
                        ? "bg-black text-white"
                        : inStock
                          ? "text-black hover:bg-black hover:text-white"
                          : "text-[#bbb] line-through cursor-not-allowed"
                    }`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Meta rows */}
      <div className="px-3 pb-4 pt-3 sm:px-4">
        <div className="flex items-baseline justify-between gap-2">
          <Link to={`/products/${handle}`} className="min-w-0 flex-1">
            <h3 className="truncate text-[12px] font-medium text-black">{baseTitle}</h3>
          </Link>
          <span className="shrink-0 text-[12px] font-normal text-black">
            {formatStorefrontPrice(price, currency)}
          </span>
        </div>
        {colourName && (
          <p className="mt-1 truncate text-[12px] font-normal capitalize text-[#737373]">{colourName}</p>
        )}
        {swatches.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5">
            {swatches.slice(0, 5).map((s) => (
              <span
                key={s.id}
                className="h-2.5 w-2.5 rounded-full border border-black/10"
                style={{ backgroundColor: getColorHex(s.color) }}
                title={s.color || undefined}
              />
            ))}
            <span className="ml-0.5 text-[10px] text-[#737373]">
              {swatches.length} {locale === "vi" ? "Màu" : swatches.length === 1 ? "Colour" : "Colours"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProductRail({
  products,
  siblingsMap,
  locale,
  link,
}: {
  products: ProductCardProduct[];
  siblingsMap?: Map<string, ProductCardSibling[]>;
  locale: Locale;
  link?: { label: string; to: string };
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLSpanElement>(null);
  // Same lean-and-flex physics as the style carousel (useFlexScroll).
  const { dragging } = useFlexScroll(trackRef, {
    cardSelector: ".df-rail-cell",
    mediaSelector: ".df-rail-media",
    progressRef,
    rotate: 12,
    parallax: 10,
    mediaScale: 1.05,
  });
  if (!products || products.length === 0) return null;
  return (
    <section>
      <div ref={trackRef} className={`df-rail df-rail-flex ${dragging ? "is-dragging" : ""}`}>
        {products.filter(Boolean).map((p) => (
          <RailCell key={p.id} product={p} siblings={siblingsMap?.get(p.id)} locale={locale} />
        ))}
      </div>
      <div className="fx-rail" aria-hidden="true"><span ref={progressRef} className="fx-progress" /></div>
      {link && (
        <div className="flex justify-center py-6 sm:py-8">
          <Link to={link.to} className="df-arrow-link">
            <span aria-hidden="true">→</span> {link.label}
          </Link>
        </div>
      )}
    </section>
  );
}
